import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  Typography,
  Paper,
  Alert,
  TextField,
  InputAdornment,
  Button,
  Chip,
  Card,
  CardContent,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  CircularProgress,
  Tooltip,
  Stack,
  Switch,
  Divider
} from '@mui/material';
import {
  Search,
  Refresh,
  CloudSync,
  CheckCircle,
  Cancel,
  Assignment,
  Clear
} from '@mui/icons-material';
import { DataGrid } from '@mui/x-data-grid';
import axios from 'axios';

const InventoryPage = () => {
  const [inventory, setInventory] = useState([]);
  const [fulfillmentCenters, setFulfillmentCenters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncingShopify, setSyncingShopify] = useState(false);
  const [filters, setFilters] = useState({
    search: '',
    activation_status: '',
    vendor: '',
    fulfillment_center: ''
  });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [stats, setStats] = useState({
    total_products: 0,
    fully_active: 0,
    partially_active: 0,
    inactive: 0,
    total_activations: 0
  });

  const [productDetailsOpen, setProductDetailsOpen] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [productOrders, setProductOrders] = useState([]);
  const [loadingOrders, setLoadingOrders] = useState(false);

  // Define fetchInventory first before using it in other hooks
  const fetchInventory = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      
      Object.entries(filters).forEach(([key, value]) => {
        if (value) {
          params.append(key, value);
        }
      });

      const response = await axios.get(`/api/inventory?${params.toString()}`);
      setInventory(response.data.inventory || []);
      setFulfillmentCenters(response.data.fulfillment_centers || []);
      setStats(response.data.stats || {
        total_products: 0,
        fully_active: 0,
        partially_active: 0,
        inactive: 0,
        total_activations: 0
      });
      
      setError('');
      
    } catch (error) {
      console.error('Failed to fetch inventory:', error);
      
      if (error.response?.status === 401) {
        setError('Unauthorized. Please login again.');
      } else if (error.response?.data?.message) {
        setError(`Failed to load inventory: ${error.response.data.message}`);
      } else {
        setError('Unable to load inventory. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  }, [filters]); // Only depend on filters, not stats or error

  // Debounced search function - create once with useMemo to avoid recreating on every render
  const debouncedFetchInventory = useMemo(
    () => debounce(() => {
      fetchInventory();
    }, 300),
    [fetchInventory]
  );

  // Auto-trigger search when filters change
  useEffect(() => {
    debouncedFetchInventory();
  }, [debouncedFetchInventory]);

  // Initial load only - no dependency on fetchInventory to avoid double calls
  useEffect(() => {
    const initialLoad = async () => {
      try {
        setLoading(true);
        const response = await axios.get('/api/inventory');
        setInventory(response.data.inventory || []);
        setFulfillmentCenters(response.data.fulfillment_centers || []);
        setStats(response.data.stats || {
          total_products: 0,
          fully_active: 0,
          partially_active: 0,
          inactive: 0,
          total_activations: 0
        });
        setError('');
      } catch (error) {
        console.error('Failed to fetch inventory:', error);
        if (error.response?.status === 401) {
          setError('Unauthorized. Please login again.');
        } else if (error.response?.data?.message) {
          setError(`Failed to load inventory: ${error.response.data.message}`);
        } else {
          setError('Unable to load inventory. Please try again.');
        }
      } finally {
        setLoading(false);
      }
    };
    
    initialLoad();
  }, []); // Empty dependency array for initial load only

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleClearFilters = () => {
    setFilters({
      search: '',
      activation_status: '',
      vendor: '',
      fulfillment_center: ''
    });
  };

  const syncFromShopify = async () => {
    try {
      setSyncingShopify(true);
      setError('');
      const response = await axios.post('/api/inventory/sync/shopify');
      setSuccess(`Shopify sync completed: ${response.data.results.created} created, ${response.data.results.updated} updated`);
      fetchInventory();
    } catch (error) {
      console.error('Failed to sync from Shopify:', error);
      setError('Failed to sync products from Shopify');
    } finally {
      setSyncingShopify(false);
    }
  };

  const toggleProductActivation = async (productId, centerId, centerCode) => {
    try {
      const response = await axios.post(
        `/api/inventory/product/${productId}/fulfillment-center/${centerId}/toggle`
      );
      
      setSuccess(`Product ${response.data.activation.is_active ? 'activated' : 'deactivated'} for ${centerCode}`);
      fetchInventory(); // Refresh data
      
    } catch (error) {
      console.error('Failed to toggle activation:', error);
      setError('Failed to toggle product activation');
    }
  };

  const fetchProductOrders = async (productId) => {
    try {
      setLoadingOrders(true);
      const response = await axios.get(`/api/inventory/product/${productId}/orders`);
      setProductOrders(response.data);
    } catch (error) {
      console.error('Failed to fetch product orders:', error);
      setError('Failed to load product order details');
    } finally {
      setLoadingOrders(false);
    }
  };

  const handleProductClick = (product) => {
    setSelectedProduct(product);
    setProductDetailsOpen(true);
    fetchProductOrders(product.id);
  };

  const handleCloseProductDetails = () => {
    setProductDetailsOpen(false);
    setSelectedProduct(null);
    setProductOrders([]);
  };

  const getActivationStatusColor = (status) => {
    switch (status) {
      case 'active': return 'success';
      case 'partial': return 'warning';
      case 'inactive': return 'error';
      default: return 'default';
    }
  };

  const getActivationStatusIcon = (status) => {
    switch (status) {
      case 'active': return <CheckCircle color="success" />;
      case 'partial': return <Assignment color="warning" />;
      case 'inactive': return <Cancel color="error" />;
      default: return null;
    }
  };

  const renderFulfillmentCenterCell = (product, centerCode, centerData) => (
    <Box key={centerCode} sx={{ display: 'flex', flexDirection: 'column', gap: 1, minWidth: 120 }}>
      {/* Activation Toggle */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Switch
          checked={centerData.is_active}
          onChange={() => toggleProductActivation(product.id, centerData.center_id, centerCode)}
          size="small"
          color="primary"
        />
        <Typography variant="caption" color={centerData.is_active ? 'success.main' : 'text.secondary'}>
          {centerData.is_active ? 'Active' : 'Inactive'}
        </Typography>
      </Box>
    </Box>
  );

  const columns = [
    {
      field: 'image_url',
      headerName: 'Image',
      width: 80,
      sortable: false,
      renderCell: (params) => (
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center',
          height: '100%'
        }}>
          {params.value ? (
            <img
              src={params.value}
              alt={params.row.title}
              style={{
                width: 50,
                height: 50,
                objectFit: 'cover',
                borderRadius: 4,
                border: '1px solid #e0e0e0'
              }}
              onError={(e) => {
                e.target.style.display = 'none';
                e.target.nextSibling.style.display = 'flex';
              }}
            />
          ) : (
            <Box sx={{ 
              width: 50, 
              height: 50, 
              backgroundColor: '#f5f5f5',
              borderRadius: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              border: '1px solid #e0e0e0'
            }}>
              <Typography variant="caption" color="text.disabled">
                No Image
              </Typography>
            </Box>
          )}
          {/* Fallback for broken images */}
          <Box sx={{ 
            width: 50, 
            height: 50, 
            backgroundColor: '#f5f5f5',
            borderRadius: 1,
            display: 'none',
            alignItems: 'center',
            justifyContent: 'center',
            border: '1px solid #e0e0e0'
          }}>
            <Typography variant="caption" color="text.disabled">
              No Image
            </Typography>
          </Box>
        </Box>
      )
    },
    {
      field: 'sku',
      headerName: 'SKU',
      width: 150,
      renderCell: (params) => (
        <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
          {params.value}
        </Typography>
      )
    },
    {
      field: 'title',
      headerName: 'Product Title',
      width: 250,
      renderCell: (params) => (
        <Tooltip title={params.value}>
          <Typography variant="body2" sx={{ 
            overflow: 'hidden', 
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap'
          }}>
            {params.value}
          </Typography>
        </Tooltip>
      )
    },
    {
      field: 'vendor',
      headerName: 'Vendor',
      width: 120
    },
    {
      field: 'activation_status',
      headerName: 'Status',
      width: 120,
      renderCell: (params) => (
        <Chip
          icon={getActivationStatusIcon(params.value)}
          label={params.value.charAt(0).toUpperCase() + params.value.slice(1)}
          color={getActivationStatusColor(params.value)}
          size="small"
        />
      )
    },
    {
      field: 'active_centers_count',
      headerName: 'Active Centers',
      width: 120,
      renderCell: (params) => (
        <Typography variant="body2">
          {params.value} / {params.row.total_centers_count}
        </Typography>
      )
    },
    ...fulfillmentCenters.map(center => ({
      field: `center_${center.code}`,
      headerName: center.name,
      width: 150,
      sortable: false,
      renderCell: (params) => {
        const centerData = params.row.fulfillment_centers[center.code];
        return renderFulfillmentCenterCell(params.row, center.code, centerData);
      }
    }))
  ];

  const rows = inventory.map(item => ({
    id: item.id,
    ...item,
    ...fulfillmentCenters.reduce((acc, center) => {
      acc[`center_${center.code}`] = item.fulfillment_centers[center.code];
      return acc;
    }, {})
  }));

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        Product Activation Management
      </Typography>
      <Typography variant="subtitle1" color="text.secondary" gutterBottom>
        Manage product activation status and rule-based quantity assignments across fulfillment centers
      </Typography>

      {/* Alerts */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={2.4}>
          <Card>
            <CardContent>
              <Typography color="text.secondary" gutterBottom variant="h6">
                Total Products
              </Typography>
              <Typography variant="h4">
                {stats.total_products}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <Card>
            <CardContent>
              <Typography color="text.secondary" gutterBottom variant="h6">
                Fully Active
              </Typography>
              <Typography variant="h4" color="success.main">
                {stats.fully_active}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <Card>
            <CardContent>
              <Typography color="text.secondary" gutterBottom variant="h6">
                Partially Active
              </Typography>
              <Typography variant="h4" color="warning.main">
                {stats.partially_active}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <Card>
            <CardContent>
              <Typography color="text.secondary" gutterBottom variant="h6">
                Inactive
              </Typography>
              <Typography variant="h4" color="error.main">
                {stats.inactive}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <Card>
            <CardContent>
              <Typography color="text.secondary" gutterBottom variant="h6">
                Total Activations
              </Typography>
              <Typography variant="h4">
                {stats.total_activations}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Fulfillment Center Order Statistics */}
      {stats.fulfillment_center_stats && Object.keys(stats.fulfillment_center_stats).length > 0 && (
        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid item xs={12}>
            <Typography variant="h6" gutterBottom>
              Order Statistics by Fulfillment Center
            </Typography>
          </Grid>
          {Object.entries(stats.fulfillment_center_stats).map(([centerCode, centerStats]) => (
            <Grid item xs={12} sm={6} md={3} key={centerCode}>
              <Card variant="outlined">
                <CardContent>
                  <Typography color="text.secondary" gutterBottom variant="subtitle2">
                    {centerStats.name}
                  </Typography>
                  <Typography variant="h5" color="primary">
                    {centerStats.order_count}
                  </Typography>
                  <Typography color="text.secondary" variant="body2">
                    orders assigned
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
                     ))}
         </Grid>
       )}

      {/* Filters and Actions */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
          <TextField
            size="small"
            placeholder="Search SKU, title, or vendor..."
            value={filters.search}
            onChange={(e) => handleFilterChange('search', e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search />
                </InputAdornment>
              )
            }}
            sx={{ minWidth: 250 }}
          />

          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Activation Status</InputLabel>
            <Select
              value={filters.activation_status}
              label="Activation Status"
              onChange={(e) => handleFilterChange('activation_status', e.target.value)}
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value="active">Fully Active</MenuItem>
              <MenuItem value="partial">Partially Active</MenuItem>
              <MenuItem value="inactive">Inactive</MenuItem>
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Fulfillment Center</InputLabel>
            <Select
              value={filters.fulfillment_center}
              label="Fulfillment Center"
              onChange={(e) => handleFilterChange('fulfillment_center', e.target.value)}
            >
              <MenuItem value="">All Centers</MenuItem>
              {fulfillmentCenters.map(center => (
                <MenuItem key={center.code} value={center.code}>
                  {center.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Button
            variant="outlined"
            startIcon={<Clear />}
            onClick={handleClearFilters}
          >
            Clear Filters
          </Button>

          <Button
            variant="contained"
            startIcon={<Refresh />}
            onClick={fetchInventory}
          >
            Refresh
          </Button>

          <Button
            variant="outlined"
            startIcon={syncingShopify ? <CircularProgress size={20} /> : <CloudSync />}
            onClick={syncFromShopify}
            disabled={syncingShopify}
          >
            Sync Shopify
          </Button>
        </Stack>
      </Paper>

      {/* Data Grid */}
      <Paper sx={{ height: 600, width: '100%' }}>
        <DataGrid
          rows={rows}
          columns={columns}
          pageSize={25}
          rowsPerPageOptions={[25, 50, 100]}
          loading={loading}
          disableSelectionOnClick
          rowHeight={70}
          onRowClick={(params) => handleProductClick(params.row)}
          sx={{
            '& .MuiDataGrid-cell': {
              py: 1,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center'
            },
            '& .MuiDataGrid-row:hover': {
              backgroundColor: 'action.hover'
            }
          }}
        />
      </Paper>

      {/* Product Details Modal */}
      <Dialog
        open={productDetailsOpen}
        onClose={handleCloseProductDetails}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">
              Product Order Details
            </Typography>
            <Button onClick={handleCloseProductDetails}>Close</Button>
          </Box>
        </DialogTitle>
        <DialogContent>
          {loadingOrders ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
              <CircularProgress />
            </Box>
          ) : selectedProduct && productOrders ? (
            <Box>
              {/* Product Information */}
              <Card sx={{ mb: 3 }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    {productOrders.product?.title || selectedProduct.title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    SKU: {productOrders.product?.sku || selectedProduct.sku}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Vendor: {selectedProduct.vendor}
                  </Typography>
                </CardContent>
              </Card>

              {/* Order Statistics */}
              <Typography variant="h6" gutterBottom>
                Order Statistics
              </Typography>
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={6} md={3}>
                  <Card variant="outlined">
                    <CardContent>
                      <Typography color="text.secondary" gutterBottom variant="subtitle2">
                        Total Orders
                      </Typography>
                      <Typography variant="h4" color="primary">
                        {productOrders.order_statistics?.total_orders || 0}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={6} md={3}>
                  <Card variant="outlined">
                    <CardContent>
                      <Typography color="text.secondary" gutterBottom variant="subtitle2">
                        Total Quantity
                      </Typography>
                      <Typography variant="h4" color="primary">
                        {productOrders.order_statistics?.total_quantity_ordered || 0}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>

              {/* Fulfillment Center Breakdown */}
              {productOrders.order_statistics?.center_breakdown && Object.keys(productOrders.order_statistics.center_breakdown).length > 0 && (
                <Box sx={{ mb: 3 }}>
                  <Typography variant="h6" gutterBottom>
                    Fulfillment Center Breakdown
                  </Typography>
                  <Grid container spacing={2}>
                    {Object.entries(productOrders.order_statistics.center_breakdown).map(([centerCode, stats]) => (
                      <Grid item xs={6} md={4} key={centerCode}>
                        <Card variant="outlined">
                          <CardContent>
                            <Typography color="text.secondary" gutterBottom variant="subtitle2">
                              {stats.name}
                            </Typography>
                            <Typography variant="h5" color="success.main">
                              {stats.order_count} orders
                            </Typography>
                            <Typography color="text.secondary" variant="body2">
                              {stats.total_quantity} units
                            </Typography>
                          </CardContent>
                        </Card>
                      </Grid>
                    ))}
                  </Grid>
                </Box>
              )}

              <Divider sx={{ my: 3 }} />

              {/* Orders List */}
              <Typography variant="h6" gutterBottom>
                Order History
              </Typography>
              {productOrders.orders && productOrders.orders.length > 0 ? (
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Order #</TableCell>
                      <TableCell>Customer</TableCell>
                      <TableCell>Quantity</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Fulfillment Center</TableCell>
                      <TableCell>Date</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {productOrders.orders.map((order) => (
                      <TableRow key={order.id}>
                        <TableCell>
                          <Typography variant="body2" fontWeight="medium">
                            #{order.shopify_order_number}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Box>
                            <Typography variant="body2">
                              {order.customer_name || 'N/A'}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {order.customer_email}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {order.quantity_ordered}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={order.status || 'unknown'}
                            size="small"
                            color={order.status === 'fulfilled' ? 'success' : 'default'}
                          />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {order.fulfillment_center?.name || 'Not assigned'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" color="text.secondary">
                            {new Date(order.created_at).toLocaleDateString()}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              ) : (
                <Typography color="text.secondary" sx={{ textAlign: 'center', py: 3 }}>
                  No orders found for this product.
                </Typography>
              )}
            </Box>
          ) : null}
        </DialogContent>
      </Dialog>

    </Box>
  );
};

// Debounce utility function
function debounce(func, delay) {
  let timeoutId;
  return function (...args) {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => func.apply(this, args), delay);
  };
}

export default InventoryPage; 